import { Chip, Grid2 } from "@mui/material";
import { useEffect } from "react";

import { energyTypeList, meterOnlyEnergyTypes } from "api";
import { useFiltersContext } from "components/contexts";
import { useFormatMessage } from "lang";
import { getLiveData, useAppSelector } from "store";

export const EnergyTypePicker = ({
  multiselect = false,
  meterDataAvailable,
}: {
  multiselect?: boolean;
  // When provided, drives meter-only chip visibility from the actual
  // records being viewed rather than the momentary live-data source. A
  // dual-source plant whose meter is silent right now still has historical
  // meter records, so the live source is the wrong signal on the records
  // page. Undefined keeps the legacy live-data behavior (statistics page).
  meterDataAvailable?: boolean;
}) => {
  const formatMessage = useFormatMessage();
  const { energyType, setEnergyType } = useFiltersContext();
  const liveData = useAppSelector(getLiveData);

  // Records contain no meter data → the plant is inverter-only for this
  // view: hide the meter-only chips entirely rather than greying them.
  const hideMeterOnly = meterDataAvailable === false;
  // Legacy fallback (no record-derived signal, e.g. statistics page): grey
  // out meter-only metrics while the live snapshot is inverter-sourced.
  const disableMeterOnly =
    meterDataAvailable === undefined && liveData?.source === "inverter";

  // If the active selection just became unavailable (records turned out to
  // be inverter-only), fall back to production so the chart isn't empty.
  useEffect(() => {
    if (
      hideMeterOnly &&
      energyType.some((item) => meterOnlyEnergyTypes.has(item))
    ) {
      setEnergyType(["energyProduced"]);
    }
  }, [hideMeterOnly, energyType, setEnergyType]);

  return (
    <Grid2
      container
      spacing={1}
      sx={{
        justifyContent: "flex-start",
        flexWrap: "nowrap",
        overflowX: "auto",
        scrollbarGutter: "stable",
      }}
    >
      {energyTypeList.map((item) => {
        const isMeterOnly = meterOnlyEnergyTypes.has(item);
        if (isMeterOnly && hideMeterOnly) return null;

        const isSelected = energyType.includes(item);
        const disabled = isMeterOnly && disableMeterOnly;

        const onClick = () => {
          if (disabled) return;
          if (!multiselect) setEnergyType([item]);
          else if (isSelected)
            setEnergyType(energyType.filter((i) => i !== item));
          else setEnergyType([...energyType, item]);
        };

        return (
          <Chip
            key={item}
            variant={isSelected ? "filled" : "outlined"}
            label={formatMessage(item)}
            onClick={onClick}
            disabled={disabled}
          />
        );
      })}
    </Grid2>
  );
};
